import { makeAutoObservable } from 'mobx'
import { SingletonRequest } from 'shared/api/lib'
import { BillingApi } from 'entities/Billing/api/billing'
import { type BillingInterval, type IResponsePlan } from 'entities/Billing/api/types'
import {
  BillingPlan,
  basicMonthlyMaxCredits,
  basicAnnualMaxCredits,
} from 'entities/Billing/model/BillingPlan'

export type IPlanInfo = {
  name: 'Basic' | 'Pro' | 'Custom' | 'Trial' | 'PAYG'
  icon: 'cube' | 'atom' | 'asterisk' | 'arrowCircleRight' | 'diamond'
  iconColor?: string
}

const trialPlanInfo: IPlanInfo = {
  name: 'Trial',
  icon: 'asterisk',
  iconColor: 'var(--amber-80)',
}

const PAYGPlanInfo: IPlanInfo = {
  name: 'PAYG',
  icon: 'arrowCircleRight',
  iconColor: 'var(--amber-80)',
}

const customPlanInfo: IPlanInfo = {
  name: 'Custom',
  icon: 'diamond',
  iconColor: 'var(--pink-70)',
}
const plansConfig: IPlanInfo[] = [
  {
    name: 'Basic',
    icon: 'cube',
    iconColor: 'var(--green-70)',
  },
  {
    name: 'Pro',
    icon: 'atom',
    iconColor: 'var(--content-brand-primary)',
  },
  trialPlanInfo,
  PAYGPlanInfo,
  customPlanInfo,
]

export class BillingPlansStore {
  private _plansMap: Map<string, BillingPlan> = new Map()

  constructor() {
    makeAutoObservable(this)
  }

  get plans() {
    return Array.from(this._plansMap.values())
  }

  get monthlyPlans() {
    return this.plans.filter((plan) => plan.interval === 'monthly')
  }

  get annualPlans() {
    return this.plans.filter((plan) => plan.interval === 'annual')
  }

  get trialPlanInfo(): IPlanInfo {
    return trialPlanInfo
  }

  get PAYGPlanInfo(): IPlanInfo {
    return PAYGPlanInfo
  }

  get customPlanInfo(): IPlanInfo {
    return customPlanInfo
  }

  private _fetchPlans = async () => {
    try {
      const { data } = await BillingApi.getPlans()

      this.addItems(data.monthly)
      this.addItems(data.annual)
    } catch (e) {
      console.log(e)
    }
  }

  fetchPlans = new SingletonRequest(this._fetchPlans, { cache: true }).request

  addItems = (response: IResponsePlan[]) => {
    response.forEach((item) => {
      this.addItem(item)
    })
  }

  addItem = (response: IResponsePlan) => {
    this._plansMap.set(response.id, new BillingPlan(response))
  }

  getItem = (id: string) => {
    return this._plansMap.get(id)
  }

  getPlanInfo = (planId: string, interval: BillingInterval): IPlanInfo | null => {
    const plan = this.getItem(planId)

    if (!plan) {
      return null
    }

    const maxCredits = interval === 'monthly' ? basicMonthlyMaxCredits : basicAnnualMaxCredits
    const planType = plan.credits <= maxCredits ? 'Basic' : 'Pro'

    return plansConfig.find((p) => p.name === planType) || null
  }
}
